import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import MenuBar from "@/components/MenuBar";
import LogTimeOffModal from "@/components/pto/LogTimeOffModal";
import { TooltipContent } from "@/components/ui/tooltip";
import {
  Tooltip,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { HelpCircle, Menu, UserIcon, Info } from "lucide-react";
import { toast } from "@/components/ui/use-toast";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { EndpointKeys } from "@/config/api";
import ApiService from "@/services/api";
import RenderAllLeavesCard from "@/components/pto/RenderAllLeavesCard";
import { Leave } from "@/components/pto/types";
import { convertFormToRequest } from "@/types/timeoff";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { parseISO } from "date-fns";
import { useAuth } from "@/hooks/useAuth";
import { User, isSuperUser } from "@/types/user";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import EmployeeSelector from "@/components/common/EmployeeSelector";
import { useEmployees } from "@/contexts/EmployeeContext";

interface PtoSettingsVersion {
  id: number;
  ukEmployerEmployeeId: number;
  ptoSettings: number;
  vacationPayConfiguration: string;
  version: number;
  name?: string;
}

interface LeaveBalance {
  policyId: number;
  name: string;
  policyDisplayName: string;
  ukEmployerEmployeeId: number;
  leaveTypeVersionId: number;
  balance: number;
}

interface LeavePolicy {
  policyId: number;
  policyDisplayName: string;
  accrualPeriod: string;
  canHaveNegativeBalance: boolean;
  accrualStartFrom: string | null;
  requiresProof: boolean;
}

interface PtoSettingsResponse {
  ptoSettings: PtoSettingsVersion[];
}

interface LeavePoliciesResponse {
  leavePolicies: LeavePolicy[];
}

interface LeaveBalancesResponse {
  leavePolicies: LeaveBalance[];
}

interface LeavesResponse {
  leaves: Leave[];
}

interface UpdatePtoSettingsRequest {
  ukEmployerEmployee: number;
  vacationPayConfiguration: string;
}

const TimeOff: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { selectedEmployeeId } = useEmployees();
  const currentYear = new Date().getFullYear();
  const defaultStartDate = parseISO(`${currentYear}-01-01`);
  const defaultEndDate = parseISO(`${currentYear}-12-31`);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<number>(0);
  const [leaveBalances, setLeaveBalances] = useState<LeaveBalance[]>([]);
  const [leaves, setLeaves] = useState<Leave[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [filterEmployee, setFilterEmployee] = useState("");
  const [filterStartDate, setFilterStartDate] =
    useState<Date>(defaultStartDate);
  const [filterEndDate, setFilterEndDate] = useState<Date>(defaultEndDate);
  const [filterStatus, setFilterStatus] = useState<string | null>(null);
  const [leavePolicies, setLeavePolicies] = useState<LeavePolicy[]>([]);
  const [employeePtoSettings, setEmployeePtoSettings] = useState<
    PtoSettingsVersion[]
  >([]);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Superuser specific state
  const isSuperUserAccount = isSuperUser(user);
  const [selectedUserId, setSelectedUserId] = useState<string>("current");
  const [users, setUsers] = useState<User[]>([]);
  const [hasSelectedUser, setHasSelectedUser] = useState(false);

  // Fetch users if superuser
  useEffect(() => {
    if (isSuperUserAccount) {
      fetchUsers();
    }
  }, [isSuperUserAccount]);

  // Set hasSelectedUser when selectedUserId changes
  useEffect(() => {
    if (isSuperUserAccount && selectedUserId !== "current") {
      setHasSelectedUser(true);
    }
  }, [selectedUserId, isSuperUserAccount]);

  const handleUserChange = (userId: string) => {
    setSelectedUserId(userId);
    const newUrl = new URL(window.location.href);
    if (userId !== "current") {
      newUrl.searchParams.set("userId", userId);
    } else {
      newUrl.searchParams.delete("userId");
    }
    window.history.replaceState({}, "", newUrl.toString());
  };

  const fetchUsers = async () => {
    try {
      const response = await ApiService.request<{ users: User[] }>(
        EndpointKeys.FETCH_ALL_USERS,
      );
      setUsers(response.users);
    } catch (error) {
      console.error("Failed to fetch users:", error);
    }
  };

  useEffect(() => {
    if (!isSuperUserAccount || hasSelectedUser) {
      fetchInitialData();
    }
  }, [selectedUserId, isSuperUserAccount, hasSelectedUser]);

  const fetchInitialData = async () => {
    await Promise.all([
      fetchLeaveBalances(),
      fetchLeaves(),
      fetchLeavePolicies(),
      fetchPtoSettings(),
    ]);
  };

  // Rest of the fetch functions need to be updated to include userId parameter
  const fetchPtoSettings = async () => {
    try {
      const queryParams: Record<string, string | number> = {};
      if (selectedUserId !== "current") {
        queryParams.userId = selectedUserId;
      }

      const data = await ApiService.request<PtoSettingsResponse>(
        EndpointKeys.FETCH_PTO_SETTINGS,
        {
          queryParams,
        },
      );
      setEmployeePtoSettings(data.ptoSettings);
    } catch (error) {
      console.error("Error fetching PTO settings:", error);
      toast({
        title: "Error",
        description: "Failed to fetch PTO settings",
        variant: "destructive",
      });
    }
  };

  const handleUpdatePtoSettings = async (
    employeeId: number,
    vacationPayConfig: string,
  ) => {
    try {
      const queryParams: Record<string, string | number> = {};
      if (selectedUserId !== "current") {
        queryParams.userId = selectedUserId;
      }

      await ApiService.request<void>(EndpointKeys.UPDATE_PTO_SETTINGS, {
        method: "POST",
        body: {
          ukEmployerEmployee: employeeId,
          vacationPayConfiguration: vacationPayConfig,
        } as UpdatePtoSettingsRequest,
        queryParams,
      });

      await fetchPtoSettings();
      toast({
        title: "Success",
        description: "Vacation pay settings updated successfully",
      });
    } catch (error) {
      console.error("Error updating settings:", error);
      toast({
        title: "Error",
        description: "Failed to update vacation pay settings",
        variant: "destructive",
      });
    }
  };

  const fetchLeaveBalances = async () => {
    try {
      const queryParams: Record<string, string | number> = {};
      if (selectedUserId !== "current") {
        queryParams.userId = selectedUserId;
      }
      if (selectedEmployeeId != null) {
        queryParams.employeeId = selectedEmployeeId;
      }

      const data = await ApiService.request<LeaveBalancesResponse>(
        EndpointKeys.FETCH_LEAVE_POLICIES,
        {
          queryParams,
        },
      );
      setLeaveBalances(data.leavePolicies);
    } catch (error) {
      console.error("Error fetching leave balances:", error);
    }
  };

  const fetchLeaves = async () => {
    setIsLoading(true);
    try {
      const queryParams: Record<string, string | number> = {
        startDate: filterStartDate.toISOString().split("T")[0],
        endDate: filterEndDate.toISOString().split("T")[0],
      };

      if (selectedUserId !== "current") {
        queryParams.userId = selectedUserId;
      }
      if (selectedEmployeeId != null) {
        queryParams.employeeId = selectedEmployeeId;
      }

      const data = await ApiService.request<LeavesResponse>(
        EndpointKeys.FETCH_LEAVES,
        {
          queryParams,
        },
      );
      setLeaves(data.leaves);
    } catch (error) {
      console.error("Error fetching leaves:", error);
      toast({
        title: "Error",
        description: "Failed to fetch leave data",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchLeavePolicies = async () => {
    try {
      const queryParams: Record<string, string | number> = {};
      if (selectedUserId !== "current") {
        queryParams.userId = selectedUserId;
      }
      if (selectedEmployeeId != null) {
        queryParams.employeeId = selectedEmployeeId;
      }

      const data = await ApiService.request<LeavePoliciesResponse>(
        EndpointKeys.FETCH_LEAVE_POLICIES,
        {
          queryParams,
        },
      );
      setLeavePolicies(data.leavePolicies);
    } catch (error) {
      console.error("Error fetching leave policies:", error);
    }
  };

  // User selector component for superusers
  const SuperUserSelector = () => {
    if (!isSuperUserAccount) return null;

    const isFirstVisit = selectedUserId === "current";

    return (
      <div className="w-full md:w-64 mb-6">
        <div className="flex items-center mb-1">
          <UserIcon className="w-4 h-4 mr-2 text-blue-500" />
          <label className="text-sm font-medium text-gray-700">
            View as User
          </label>
        </div>
        <Select value={selectedUserId} onValueChange={handleUserChange}>
          <SelectTrigger
            className={
              isFirstVisit ? "border-blue-500 ring-2 ring-blue-200" : ""
            }
          >
            <SelectValue placeholder="⚠️ Select a user to view" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="current">Current User (You)</SelectItem>
            {users.map((user) => (
              <SelectItem key={user.id} value={user.id.toString()}>
                {user.email}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {isFirstVisit && (
          <p className="mt-1 text-xs text-blue-600">
            ⚠️ Please select a user to proceed
          </p>
        )}
      </div>
    );
  };

  const SettingsTable = () => (
    <Card>
      <CardHeader>
        <div className="flex items-center space-x-2">
          <h3 className="text-lg font-semibold">Employee PTO Settings</h3>
        </div>
        <p className="text-sm text-muted-foreground pt-1">
          Configure how vacation pay should be handled for each employee:
        </p>
        <ul className="list-disc pl-5 mt-2 text-sm text-muted-foreground">
          <li>
            <strong>Separate:</strong> Vacation pay calculated and paid
            separately.
          </li>
          <li>
            <strong>Not Separate:</strong> Vacation pay included in regular
            wages.
          </li>
        </ul>
      </CardHeader>
      <CardContent className="overflow-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Employee Name</TableHead>
              <TableHead>Vacation Pay Configuration</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {employeePtoSettings.map((setting) => (
              <TableRow key={setting.ukEmployerEmployeeId}>
                <TableCell>{setting.name}</TableCell>
                <TableCell>
                  <Select
                    defaultValue={setting.vacationPayConfiguration}
                    onValueChange={(value) =>
                      handleUpdatePtoSettings(
                        setting.ukEmployerEmployeeId,
                        value,
                      )
                    }
                  >
                    <SelectTrigger className="w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Separate">Separate</SelectItem>
                      <SelectItem value="NotSeparate">Not Separate</SelectItem>
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell>
                  <Button
                    variant="secondary"
                    onClick={() =>
                      navigate(`/employee/${setting.ukEmployerEmployeeId}`)
                    }
                  >
                    View Employee
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );

  if (isLoading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-background">
      {/* Mobile Menu Toggle */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-30 flex items-center p-4 bg-gray-900">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="text-white"
        >
          <Menu className="h-6 w-6" />
        </Button>
      </div>

      {/* Sidebar */}
      <div
        className={`${
          isMobileMenuOpen ? "block" : "hidden"
        } md:block fixed md:relative w-64 h-full bg-gray-900 z-20`}
      >
        <MenuBar />
      </div>

      {/* Main Content */}
      <div className="flex-1 w-full md:w-auto mt-16 md:mt-0 overflow-auto">
        <div className="p-4 md:p-8">
          <div className="flex flex-col md:flex-row md:items-start justify-between mb-8">
            <div className="space-y-6">
              <h1 className="text-3xl font-bold">Time Off</h1>
              <Alert className="my-6">
                <Info className="h-4 w-4 text-primary" />
                <AlertTitle>Time Off Management</AlertTitle>
                <AlertDescription>
                  Manage employee time off requests, view balances, configure
                  leave policies, and approve PTO.
                </AlertDescription>
              </Alert>
            </div>
            {/* Add SuperUserSelector here */}
            <SuperUserSelector />
          </div>

          <div className="mb-4">
            <EmployeeSelector label="Filter by employee" placeholder="All employees" />
          </div>

          <Card>
            <CardContent className="pt-6">
              <Tabs defaultValue="timeoff" className="space-y-6">
                <TabsList className="w-full justify-start overflow-auto">
                  <TabsTrigger value="timeoff">Time Off</TabsTrigger>
                  <TabsTrigger value="policies">Policies</TabsTrigger>
                  <TabsTrigger value="settings">Settings</TabsTrigger>
                </TabsList>

                <TabsContent value="timeoff">
                  <div className="space-y-6">
                    <Card>
                      <CardHeader className="flex flex-row items-center justify-between">
                        <h2 className="text-xl">Time Off Balances</h2>
                        <Button onClick={() => setIsModalOpen(true)}>
                          Log Time Off
                        </Button>
                      </CardHeader>
                      <CardContent className="overflow-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Employee Name</TableHead>
                              <TableHead>Leave Policy</TableHead>
                              <TableHead>
                                {new Date().getFullYear()} Balance (Hours)
                              </TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {leaveBalances.map((balance, index) => (
                              <TableRow key={index}>
                                <TableCell>{balance.name}</TableCell>
                                <TableCell>
                                  {balance.policyDisplayName}
                                </TableCell>
                                <TableCell>
                                  {balance.balance < 999999
                                    ? Math.round(balance.balance * 100) / 100
                                    : "Unlimited"}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </CardContent>
                    </Card>
                    <RenderAllLeavesCard
                      filterEmployee={filterEmployee}
                      setFilterEmployee={setFilterEmployee}
                      filterStartDate={filterStartDate}
                      setFilterStartDate={(date: Date | undefined) => {
                        setFilterStartDate(date || defaultStartDate);
                      }}
                      filterEndDate={filterEndDate}
                      setFilterEndDate={(date: Date | undefined) => {
                        setFilterEndDate(date || defaultEndDate);
                      }}
                      leaves={leaves}
                      onUpdate={fetchLeaves}
                      isLoading={isLoading}
                    />
                  </div>
                </TabsContent>

                <TabsContent value="policies">
                  <Card>
                    <CardHeader>
                      <h2 className="text-xl font-semibold">Leave Policies</h2>
                    </CardHeader>
                    <CardContent className="overflow-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Policy Name</TableHead>
                            <TableHead>Accrual Period</TableHead>
                            <TableHead>Negative Balance Allowed</TableHead>
                            <TableHead>Accrual Start</TableHead>
                            <TableHead>Requires Proof</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {leavePolicies.map((policy) => (
                            <TableRow
                              key={policy.policyId}
                              className="cursor-pointer hover:bg-gray-100"
                              onClick={() => {
                                // Include userId in query params if in superuser mode
                                if (
                                  isSuperUserAccount &&
                                  selectedUserId !== "current"
                                ) {
                                  navigate(
                                    `/time-off/policy/${policy.policyId}?userId=${selectedUserId}`,
                                  );
                                } else {
                                  navigate(
                                    `/time-off/policy/${policy.policyId}`,
                                  );
                                }
                              }}
                            >
                              <TableCell>{policy.policyDisplayName}</TableCell>
                              <TableCell>{policy.accrualPeriod}</TableCell>
                              <TableCell>
                                {policy.canHaveNegativeBalance ? "Yes" : "No"}
                              </TableCell>
                              <TableCell>
                                {policy.accrualStartFrom || "N/A"}
                              </TableCell>
                              <TableCell>
                                {policy.requiresProof ? "Yes" : "No"}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="settings">
                  <SettingsTable />
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>
      </div>

      <LogTimeOffModal
        leaveBalances={leaveBalances}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={async (values) => {
          try {
            const request = convertFormToRequest(values);

            const queryParams: Record<string, string | number> = {};
            if (selectedUserId !== "current") {
              queryParams.userId = selectedUserId;
            }

            await ApiService.request(EndpointKeys.LOG_TIME_OFF, {
              method: "POST",
              body: request,
              queryParams,
            });
            await fetchLeaves();
            setIsModalOpen(false);
            toast({
              title: "Success",
              description: "Time off logged successfully",
            });
          } catch (error) {
            console.error("Error logging time off:", error);
            toast({
              title: "Error",
              description: "Failed to log time off",
              variant: "destructive",
            });
          }
        }}
      />
    </div>
  );
};

export default TimeOff;
